"use client"
import { Button } from "@/components/ui/button"
import { ArrowRight, BookOpen, ChevronDown } from "lucide-react"
import { motion } from "framer-motion"
import { useEffect, useState } from "react"
import Link from "next/link"

export interface LatestPost {
  slug: string
  title: string
}

// The pill used to hard-code "Latest: AI Fine-Tuning for SMEs" and link to
// /blog. That post was from April 2025 and two newer ones had been published
// since, so the one thing on the page advertising freshness was the most
// obviously stale. It now comes from posts/ and links to the post itself.
export default function HeroSection({ latestPost }: { latestPost?: LatestPost }) {
  const [scrollIndicator, setScrollIndicator] = useState(true)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrollIndicator(window.scrollY <= 100)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    // svh, not vh: mobile browsers size vh to the viewport with the URL bar
    // *hidden*, so a vh-sized hero overflows on load and reflows as the user
    // scrolls. svh is the small (chrome-visible) viewport and stays put.
    <section className="relative min-h-[90svh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/10 to-background" />
      <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center bg-[length:60px_60px] [mask-image:linear-gradient(180deg,black_0%,transparent_70%)] opacity-[0.06] dark:opacity-[0.08]" />
      <div className="absolute inset-0 grain-overlay" aria-hidden />
      
      <div className="container relative z-10 px-4 py-16 md:py-24 lg:py-32 text-center max-w-4xl mx-auto">
        <motion.div
          initial={{ y: 16 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block px-4 py-2 mb-5 md:mb-6 text-sm font-medium rounded-full bg-primary/10 text-primary border border-primary/10 dark:border-primary/20"
        >
          Welcome to my digital space
        </motion.div>
        
        <motion.h1
          initial={{ y: 16 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight mb-4 md:mb-6 leading-[1.15]"
        >
          {/* The old <br className="hidden xs:block sm:hidden"> never rendered:
              there is no `xs` breakpoint in tailwind.config.js, so it resolved
              to a permanently-hidden element at every width. */}
          I build AI and data solutions for{' '}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-emerald-500 to-teal-400">
            the fight against financial crime
          </span>
        </motion.h1>

        <motion.p
          initial={{ y: 16 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="text-base md:text-lg lg:text-xl text-muted-foreground mb-6 md:mb-10 max-w-2xl mx-auto px-1 leading-relaxed"
        >
          I&apos;m Sakthiish Vijayadass — technical presales at NICE Actimize · ex-Deloitte · founder — designing
          fraud, AML and GenAI solutions for banks across APAC.
        </motion.p>

        <motion.div 
          className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-10 md:mb-16"
          initial={{ y: 16 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* The hero previously offered no route to hiring or contact at all —
              the two actions were "see more of this page" and "read the blog".
              /hire is now the primary action for the visitor who arrived by
              searching the name. */}
          <Button size="lg" className="w-full sm:w-auto font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/25 transition-shadow" asChild>
            <Link href="/hire" className="group inline-flex items-center">
              Work With Me
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </Link>
          </Button>
          <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium border-2 hover:bg-secondary/80" asChild>
            <Link href="#projects" className="group inline-flex items-center">
              View My Work
            </Link>
          </Button>
          <Button variant="ghost" size="lg" className="w-full sm:w-auto font-medium" asChild>
            <Link
              href="/blog"
              className="group"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <BookOpen className={`mr-2 h-5 w-5 ${isHovered ? 'animate-bounce' : ''}`} />
              Read My Blog
            </Link>
          </Button>
        </motion.div>

        {latestPost && (
          <motion.div
            className="flex justify-center"
            initial={{ y: 12 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              href={`/blog/${latestPost.slug}`}
              className="inline-flex min-h-[44px] sm:min-h-0 max-w-full items-center gap-2 px-4 py-2.5 sm:py-2 bg-secondary/60 hover:bg-secondary dark:bg-secondary/30 dark:hover:bg-secondary/50 rounded-full border border-border/50 transition-colors"
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-accent animate-pulse" />
              {/* Post titles run long — truncate rather than let the pill wrap
                  to three lines or push the hero past the viewport. */}
              <span className="truncate text-xs md:text-sm font-medium">
                Latest: {latestPost.title}
              </span>
            </Link>
          </motion.div>
        )}
      </div>

      {scrollIndicator && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          // Hidden below sm: with three stacked full-width CTAs the hero is
          // tall enough on a phone that this collided with the "Latest" pill,
          // and "you can scroll" is not news on a touch device.
          className="absolute bottom-10 left-1/2 hidden -translate-x-1/2 flex-col items-center sm:flex"
        >
          <span className="text-sm text-muted-foreground mb-2">Scroll to explore</span>
          <ChevronDown className="h-6 w-6 animate-bounce text-muted-foreground" />
        </motion.div>
      )}
    </section>
  )
}
